import { Trans, useTranslation } from 'next-i18next'

import ChevronDownIcon from '~/components/Icons/svgs/ChevronDownIcon'
import CloseButtonIcon from '~/components/Icons/svgs/CloseButtonIcon'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'

import { useStateSwitchPrompt } from '../hooks/useStateSwitchPrompt'

// Figma names the standard without the dropdown's separator ("Florida B.E.S.T.") and lists states by name alone.
const toStandardName = (label: string): string => label.replace(' - ', ' ')
const toStateName = (label: string): string => label.split(' - ')[0]

interface Props {
  isBlocked?: boolean
}

export default function StateSwitchModal({ isBlocked = false }: Props): React.ReactElement | null {
  const { t } = useTranslation('curriculum')
  const { availableStates, chooseState, confirm, dismiss, isSwitching, losesProgress, selectedState, shouldShow } =
    useStateSwitchPrompt(isBlocked)

  if (!shouldShow || !selectedState) return null

  const standardName = toStandardName(selectedState.label)

  return (
    // Scrolls itself: the page behind is scroll-locked and the Florida card outgrows short phones.
    <aside className="fixed inset-0 z-top overflow-y-auto overscroll-contain bg-purple-abyss/60">
      <section className="flex min-h-full items-center justify-center px-4 py-5">
        <div className="flex w-full max-w-132 flex-col gap-4 rounded-20 border border-purple-glow/40 bg-purple-plum p-5 shadow-purple2 md:gap-5 md:p-7">
          <div className="flex flex-col gap-2 md:gap-2.5">
            <div className="flex h-11 items-center justify-end md:h-auto">
              <button aria-label={t('stateSwitchPrompt.close')} disabled={isSwitching} onClick={dismiss} type="button">
                <SVGIcon icon={CloseButtonIcon} pathClassName="text-white" size={18} />
              </button>
            </div>

            <h2 className="text-20px font-bold leading-1.3 text-white md:text-2xl md:leading-8">
              <Trans
                components={{ highlight: <span className="whitespace-nowrap text-green-lime" /> }}
                i18nKey="curriculum:stateSwitchPrompt.title"
                values={{ state: standardName }}
              />
            </h2>

            <p className="text-13px leading-5 text-white/85 md:text-xs md:leading-5.5 md:text-white">
              {t('stateSwitchPrompt.intro')}
            </p>
            <p className="text-13px leading-5 text-white/85 md:text-xs md:leading-5.5 md:text-white">
              {t('stateSwitchPrompt.body', { state: toStateName(selectedState.label) })}
            </p>
          </div>

          <div className="flex flex-col gap-1.5 md:gap-1.75">
            <label className="text-xxs font-semibold leading-4.5 text-white" htmlFor="state-switch-standard">
              {t('stateSwitchPrompt.fieldLabel')}
            </label>
            <div className="relative">
              <select
                className="h-11 w-full appearance-none rounded-2.5 border border-purple-glow/45 bg-purple-twilight px-3.5 text-13px font-semibold text-white md:h-11.5 md:rounded-12px md:px-4 md:text-xs"
                disabled={isSwitching}
                id="state-switch-standard"
                onChange={(event) => chooseState(event.target.value)}
                value={selectedState.curriculumName ?? ''}
              >
                {availableStates.map(({ curriculumName, label }) => (
                  <option key={curriculumName} value={curriculumName}>
                    {toStateName(label)}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 md:right-4">
                <SVGIcon icon={ChevronDownIcon} pathClassName="text-white" size={16} />
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2.5 rounded-12px border border-orange/30 bg-white p-3 text-xxs leading-4.5 text-purple-darker md:gap-1.75 md:rounded-14 md:border-orange/40 md:px-4 md:py-3.5 md:text-13px md:leading-5">
            {losesProgress && (
              <>
                <p>
                  <span className="block font-bold">{t('stateSwitchPrompt.noTransferTitle')}</span>
                  {t('stateSwitchPrompt.noTransferBody')}
                </p>
                <hr className="h-px border-0 bg-orange/15 md:hidden" />
              </>
            )}
            <p>
              <span className="block font-bold">{t('stateSwitchPrompt.nothingLostTitle')}</span>
              {losesProgress ? (
                <>
                  <span className="md:hidden">{t('stateSwitchPrompt.nothingLostBodyKeptShort')}</span>
                  <span className="hidden md:inline">{t('stateSwitchPrompt.nothingLostBodyKept')}</span>
                </>
              ) : (
                t('stateSwitchPrompt.nothingLostBody')
              )}
            </p>
          </div>

          {/* Side by side at every width; items-stretch keeps both the height of a wrapped Switch label. */}
          <div className="flex items-stretch justify-end gap-2.5">
            <button
              className="min-h-11 shrink-0 rounded-full border border-purple-glow/50 px-5 text-13px font-semibold text-purple-lavender disabled:opacity-60 md:text-xs"
              disabled={isSwitching}
              onClick={dismiss}
              type="button"
            >
              {t('stateSwitchPrompt.notNow')}
            </button>
            <button
              className="min-h-11 flex-1 rounded-full bg-purple px-5 py-2.5 text-center text-13px font-semibold leading-5 text-white transition hover:opacity-90 disabled:opacity-60 md:flex-none md:text-xs"
              disabled={isSwitching}
              onClick={confirm}
              type="button"
            >
              {isSwitching ? t('stateSwitchPrompt.switching') : t('stateSwitchPrompt.switch', { state: standardName })}
            </button>
          </div>
        </div>
      </section>
    </aside>
  )
}
